import { useEffect } from "react";
import { motion } from "framer-motion";
import Video from "./Video";
import Magnetic from "./Magnetic";
import { CALENDLY } from "../data/site";
import { lenisRef } from "./SmoothScroll";

const BLOCKS = [
  ["challenge", "CHALLENGE"],
  ["strategy", "STRATEGY"],
  ["execution", "EXECUTION"],
  ["result", "RESULT"],
];

export default function CaseStudyOverlay({ study, onClose }) {
  useEffect(() => {
    lenisRef.current?.stop();
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      lenisRef.current?.start();
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
      animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
      exit={{ clipPath: "inset(0% 0% 100% 0%)" }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      className="fixed inset-0 z-[90] overflow-y-auto bg-black"
      data-testid="case-overlay"
    >
      {/* top bar */}
      <div className="sticky top-0 z-20 flex items-center justify-between bg-black/60 px-6 py-5 backdrop-blur-xl md:px-12">
        <span className="label text-lav-400">{study.company}</span>
        <button
          onClick={onClose}
          data-cursor="OPEN"
          data-testid="case-close"
          className="label text-white hover:text-lav-400 transition-colors"
        >
          CLOSE ✕
        </button>
      </div>

      {/* hero of the case */}
      <div className="relative h-[70vh] w-full overflow-hidden">
        <Video src={study.video} priority className="h-full w-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/40" />
        <div className="absolute bottom-0 left-0 p-6 md:p-12">
          <motion.h1
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="text-huge uppercase"
          >
            {study.client}
          </motion.h1>
          <p className="mt-4 max-w-xl text-sm md:text-lg font-light text-white/60">
            {study.tagline}
          </p>
        </div>
      </div>

      {/* metric band */}
      <div className="border-y border-white/[0.08] px-6 py-16 md:px-12">
        <div className="text-metric text-lav-400">{study.metric}</div>
        <div className="label mt-4 text-white/50">{study.metricLabel}</div>
      </div>

      {/* content blocks */}
      <div className="px-6 py-20 md:px-12 md:py-28">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-2 md:gap-24">
          {BLOCKS.map(([key, title], i) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.05 }}
            >
              <div className="flex items-center gap-4 label text-white/40">
                <span className="text-lav-400">{String(i + 1).padStart(2, "0")}</span>
                <span className="h-px w-8 bg-white/20" />
                {title}
              </div>
              <p className="mt-6 text-xl md:text-3xl thin leading-snug text-white/90">
                {study[key]}
              </p>
            </motion.div>
          ))}
        </div>

        {/* cta */}
        <div className="mt-28 flex flex-col items-start gap-8 border-t border-white/[0.08] pt-16 md:flex-row md:items-center md:justify-between">
          <h3 className="text-big uppercase max-w-lg">
            WANT RESULTS LIKE <span className="text-lav-400">THIS?</span>
          </h3>
          <Magnetic strength={0.4}>
            <a
              href={CALENDLY}
              target="_blank"
              rel="noreferrer"
              data-cursor="OPEN"
              data-testid="case-cta"
              className="label group relative inline-flex items-center overflow-hidden border border-white/25 px-8 py-4 text-white"
            >
              <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                LET'S TALK ↗
              </span>
              <span className="absolute inset-0 z-0 -translate-y-full bg-lav-400 transition-transform duration-500 ease-lux group-hover:translate-y-0" />
            </a>
          </Magnetic>
        </div>
      </div>
    </motion.div>
  );
}
